import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Landmark, 
  Signal, 
  Factory,
  Scale,
  TrendingDown,
  TrendingUp,
  Quote,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const icons = [Landmark, Signal, Factory, Scale];

const UseCaseCard = ({ useCase, index, isInView, Icon, labels }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      data-testid={`usecase-card-${index + 1}`}
      className="group h-full flex flex-col"
    >
      <div 
        className="relative flex-1 bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden hover:border-blue-200 hover:shadow-xl transition-all duration-500 group-hover:-translate-y-1 flex flex-col"
      >
        {/* Top accent line */}
        <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />

        {/* Header Content */}
        <div className="p-6 md:p-8 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-900/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-blue-900/40 transition-all duration-300">
                <Icon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-zinc-100 leading-tight">{useCase.industry}</h3>
                <p className="text-sm font-medium text-zinc-400">{useCase.company}</p>
              </div>
            </div>
            <div className="sm:text-right bg-blue-900/20 sm:bg-transparent p-3 sm:p-0 rounded-xl sm:rounded-none">
              <div className="text-3xl font-extrabold text-blue-400 tracking-tight">
                {useCase.impact.primary}
              </div>
              <div className="text-xs font-semibold text-zinc-400 uppercase tracking-widest mt-1">
                {useCase.impact.label}
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <p className="text-base text-zinc-400 leading-relaxed font-medium">
              {useCase.challenge}
            </p>
          </div>
          
          {/* Cost comparison UI */}
          <div className="relative grid grid-cols-2 gap-4 mt-auto">
            {/* Visual connector between metrics */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-zinc-950 border border-zinc-800 rounded-full flex items-center justify-center z-10 shadow-sm">
              <ArrowRight className="w-3 h-3 text-zinc-300 group-hover:text-blue-500 transition-colors" />
            </div>

            <div className="bg-red-50/50 border border-red-100/50 rounded-2xl p-4 sm:p-5 relative overflow-hidden group-hover:border-red-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest">{labels.before}</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-zinc-100 tracking-tight">{useCase.before.cost}</div>
              <div className="text-xs font-medium text-zinc-400 mt-1">{labels.annualCost}</div>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-2xl p-4 sm:p-5 relative overflow-hidden group-hover:border-emerald-700 group-hover:bg-emerald-900/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">{labels.after}</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-300 tracking-tight">{useCase.after.cost}</div>
              <div className="text-xs font-medium text-emerald-400 mt-1">{labels.annualCost}</div>
            </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export const UseCasesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const data = t('useCases');

  const labels = {
    before: data.before,
    after: data.after,
    annualCost: data.annualCost,
    viewDetails: data.viewDetails,
    showLess: data.showLess,
  };

  return (
    <section
      id="usecases"
      data-testid="usecases-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-zinc-950"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        > 
          <h2
            data-testid="usecases-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
          >
            {data.headline}{' '}
            <span className="font-semibold gradient-text">
              {data.headlineHighlight}
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            {data.subheadline}
          </p>
        </motion.div>

        {/* Use Cases Grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {data.industries.map((useCase, index) => (
            <UseCaseCard
              key={index}
              useCase={useCase}
              index={index}
              isInView={isInView}
              Icon={icons[index]}
              labels={labels}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
